import { commerceApi } from '../api';
import { buildAuthHeaders } from './useAuth';
import { EndpointsMvp } from './enum';
import {
  AbandonedCartData,
  AbandonedCartNotifyState
} from '../../store/ducks/abandoned-cart/types';

interface RawAbandonedCartAddress {
  street?: string[];
  city?: string;
  postcode?: string;
  region?: string;
}

interface RawAbandonedCartPartner {
  bp?: string;
  name?: string;
  address?: RawAbandonedCartAddress;
}

interface RawAbandonedCartItem {
  sku?: string;
  name?: string;
  image?: string;
}

interface RawAbandonedCartResponse {
  quote_id?: string | number;
  sold?: RawAbandonedCartPartner;
  ship?: RawAbandonedCartPartner;
  first_item?: RawAbandonedCartItem;
  quote_count?: number;
  notificated?:
    | boolean[]
    | string[]
    | string
    | { notify_1?: boolean; notify_2?: boolean };
}

const parseNotification = (
  raw: RawAbandonedCartResponse['notificated']
): AbandonedCartNotifyState => {
  try {
    if (Array.isArray(raw) && typeof raw[0] === 'boolean') {
      return {
        notify1: raw[0] ?? false,
        notify2: (raw[1] as boolean | undefined) ?? false
      };
    }

    const first = Array.isArray(raw) ? raw[0] : undefined;
    const rawN = first ?? raw;
    const n = typeof rawN === 'string' ? JSON.parse(rawN) : rawN;
    return {
      notify1: n?.notify_1 ?? false,
      notify2: n?.notify_2 ?? false
    };
  } catch {
    return { notify1: false, notify2: false };
  }
};

const mapRawToAbandonedCartData = (
  raw: RawAbandonedCartResponse
): AbandonedCartData => ({
  quoteId: String(raw.quote_id),
  sold: {
    bp: raw.sold?.bp ?? '',
    name: raw.sold?.name ?? '',
    address: {
      street: raw.sold?.address?.street ?? [],
      city: raw.sold?.address?.city ?? '',
      postcode: raw.sold?.address?.postcode ?? '',
      region: raw.sold?.address?.region ?? ''
    }
  },
  ship: {
    bp: raw.ship?.bp ?? '',
    name: raw.ship?.name ?? '',
    address: {
      street: raw.ship?.address?.street ?? [],
      city: raw.ship?.address?.city ?? '',
      postcode: raw.ship?.address?.postcode ?? '',
      region: raw.ship?.address?.region ?? ''
    }
  },
  firstItem: {
    sku: raw.first_item?.sku ?? '',
    name: raw.first_item?.name ?? '',
    image: raw.first_item?.image ?? ''
  },
  quoteCount: raw.quote_count ?? 1,
  notification: parseNotification(raw.notificated)
});

export const fetchAbandonedCartByBps = async (
  bps: string[]
): Promise<AbandonedCartData | null> => {
  try {
    const response = await commerceApi.post(
      EndpointsMvp.ABANDONED_CART,
      { bp: bps },
      { headers: buildAuthHeaders({ withCommerceToken: true }) }
    );

    const raw = response.data as RawAbandonedCartResponse;
    if (!raw || raw.quote_id == null || raw.quote_count === 0) return null;

    return mapRawToAbandonedCartData(raw);
  } catch (err) {
    console.error('[AbandonedCart] fetchAbandonedCartByBps failed:', err);
    return null;
  }
};
